import {
  useGetEnrollmentJointManualReviewData,
  useUploadManualReviewDocument,
  useSubmitEnrollmentJointManualReviewData,
} from '@egw/api'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { UserIcon } from '@heroicons/react/16/solid'
import { inAppChatService } from '@egw/app-chat'
import { Button, ButtonVariant, GenericError, Link, Tag } from '@egw/components'
import { CUSTOMER_SERVICE_EMAIL } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { Trans, useTranslation } from '@egw/i18n'
import { EnrollmentJointLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { useNavigate } from 'react-router'
import { FileUpload, type UploadedFile } from '@egw/features-system'
import { type FC, useState, useEffect } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { PageHeader } from '../../../components'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'

const log = new Logger('ManualReviewPage')

interface FileUploadComponentProps {
  applicationId: string
  onChange: (_files: UploadedFile[]) => void
  isDisabled?: boolean
}

const FileUploadComponent: FC<FileUploadComponentProps> = ({ applicationId, onChange, isDisabled }) => {
  const { t } = useTranslation('features-enrollment')
  const { mutate: uploadDocument, isPending } = useUploadManualReviewDocument(applicationId)

  return (
    <FileUpload
      onChange={onChange}
      mutate={uploadDocument}
      listTitle={t('joint.manual-review.upload_your_documents')}
      buttonLabel={t('joint.manual-review.upload_documents')}
      testId="enrollment-manual-review-file-upload"
      isDisabled={isPending || isDisabled}
    />
  )
}

export const ManualReviewPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])

  const { data, isLoading, error } = useGetEnrollmentJointManualReviewData()
  const {
    mutate: submitEnrollmentJointManualReviewData,
    isLoading: isSubmitting,
    error: submitError,
    isSuccess: isSubmitSuccess,
    data: submitData,
  } = useSubmitEnrollmentJointManualReviewData()

  // Log initial data loading errors
  useEffect(() => {
    if (error) {
      log.error('Error loading enrollment joint manual review data', error)
    }
  }, [error])

  // Log form submission errors
  useEffect(() => {
    if (submitError) {
      log.error('Error submitting enrollment joint manual review form', submitError)
    }
  }, [submitError])

  const applicationId = data?.activePerson.applicationId ?? ''
  const profileName = data?.activePerson.name?.firstName ? `${data.activePerson.name.firstName}'s Profile` : ''
  const needManualReviewDocumentUpload = data?.activePerson.needManualReviewDocumentUpload ?? false

  // Protect route
  useValidateRoute(data?.activePerson.path)

  const handleSubmit = async (): Promise<void> => {
    try {
      const result = await submitEnrollmentJointManualReviewData({
        activePerson: {
          submittedManualReviewFilePaths: uploadedFiles.map((file) => file.filePath),
        },
      })

      // Navigate to the next step if successful and path is provided
      if (result && !result.isError && result.activePerson.path) {
        void navigate(result.activePerson.path)
      }
    } catch (err) {
      log.error('Error submitting manual review form', err)
    }
  }

  return (
    <EnrollmentJointLayout title={t('joint.manual-review.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {needManualReviewDocumentUpload ? (
        <WithUploadVariant
          applicationId={applicationId}
          profileName={profileName}
          isLoading={isLoading}
          error={error}
          data={data}
          isSubmitSuccess={isSubmitSuccess}
          isSubmitting={isSubmitting}
          uploadedFiles={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
          handleSubmit={handleSubmit}
        />
      ) : (
        <NoUploadVariant
          applicationId={applicationId}
          profileName={profileName}
          isLoading={isLoading}
          error={error}
          data={data}
          isSubmitSuccess={isSubmitSuccess}
        />
      )}

      {/* This error handles the case when the initial data is not loaded, for example network error, or service errors */}
      {(error ?? submitError ?? data?.isError ?? submitData?.isError) ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentJointLayout>
  )
}

interface NoUploadVariantProps {
  applicationId: string
  profileName: string
  isLoading: boolean
  error: unknown
  data: { isError?: boolean } | null | undefined
  isSubmitSuccess: boolean
}

const NoUploadVariant: FC<NoUploadVariantProps> = ({ applicationId, profileName, isLoading, error, data }) => {
  const { t } = useTranslation('features-enrollment')
  return (
    <>
      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error && !data?.isError && applicationId ? (
        <div className="w-full flex-1 flex flex-col justify-start">
          {profileName ? (
            <Tag icon={<UserIcon className="text-olive-400" />} className="mb-xs">
              {profileName}
            </Tag>
          ) : null}
          <PageHeader title={t('joint.manual-review.title')} className="mb-xl" />
          <p className="typo-primary-base-regular text-text-body mb-xl">
            {t('joint.manual-review.doc_no_upload_description')}
          </p>

          <p className="typo-primary-base text-text-body inline-block mb-md">
            <Trans t={t} i18nKey="joint.manual-review.secure_chat_description">
              <Link href={`mailto:${CUSTOMER_SERVICE_EMAIL}`}>{CUSTOMER_SERVICE_EMAIL}</Link>
            </Trans>
          </p>
          <div className="flex flex-col md:flex-row gap-xs mb-xl">
            <Button
              variant={ButtonVariant.Secondary}
              className="w-fit w-full md:w-[200px]"
              onClick={() => {
                sendEvent('bankhjoint_enroll_manual_review_started', {
                  applicationId,
                })
                inAppChatService.open()
              }}
            >
              {t('joint.manual-review.start_chat_cta')}
            </Button>
          </div>
        </div>
      ) : null}
    </>
  )
}

interface WithUploadVariantProps {
  applicationId: string
  profileName: string
  isLoading: boolean
  error: unknown
  data: { isError?: boolean } | null | undefined
  isSubmitSuccess: boolean
  isSubmitting: boolean
  uploadedFiles: UploadedFile[]
  setUploadedFiles: (_files: UploadedFile[]) => void
  handleSubmit: () => Promise<void>
}

const WithUploadVariant: FC<WithUploadVariantProps> = ({
  applicationId,
  profileName,
  isLoading,
  error,
  data,
  isSubmitSuccess,
  isSubmitting,
  uploadedFiles,
  setUploadedFiles,
  handleSubmit,
}) => {
  const { t } = useTranslation('features-enrollment')
  return (
    <>
      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error && !data?.isError && applicationId ? (
        <div className="w-full flex-1 flex flex-col justify-start">
          {!isSubmitSuccess ? (
            <>
              {profileName ? (
                <Tag icon={<UserIcon className="text-olive-400" />} className="mb-xs">
                  {profileName}
                </Tag>
              ) : null}
              <PageHeader title={t('joint.manual-review.title')} className="mb-xl" />

              <p className="typo-primary-base-regular text-text-body mb-2xs">
                {t('joint.manual-review.doc_upload_description')}
              </p>
              <ul className="list-disc list-inside pl-2 typo-primary-base-regular text-text-body mb-md">
                <li>{t('joint.manual-review.doc_upload_description_2')}</li>
                <li>{t('joint.manual-review.doc_upload_description_3')}</li>
                <li>{t('joint.manual-review.doc_upload_description_4')}</li>
                <li>{t('joint.manual-review.doc_upload_description_5')}</li>
                <li>{t('joint.manual-review.doc_upload_description_6')}</li>
                <li>{t('joint.manual-review.doc_upload_description_7')}</li>
                <li>{t('joint.manual-review.doc_upload_description_8')}</li>
              </ul>
              <p className="typo-primary-base-regular text-text-body mb-2xs">
                {t('joint.manual-review.broker_dealer')}
              </p>
              <ul className="list-disc list-inside pl-2 typo-primary-base-regular text-text-body mb-md">
                <li>{t('joint.manual-review.broker_dealer_1')}</li>
              </ul>
              <p className="typo-primary-base-regular text-text-body mb-md">
                {t('joint.manual-review.doc_upload_cta_description')}
              </p>
              <FileUploadComponent
                applicationId={applicationId}
                onChange={setUploadedFiles}
                isDisabled={isSubmitting}
              />
            </>
          ) : null}

          {isSubmitSuccess ? (
            <>
              <CheckCircleIcon className="h-[4rem] w-[4rem] stroke-textSuccess stroke-[.5px] mb-sm" />
              <h2 className="typo-primary-2xl-light text-textSuccess mb-md">
                {t('joint.manual-review.document_upload_complete')}
              </h2>
              <p className="typo-primary-base text-text-body mb-md">
                {t('joint.manual-review.document_upload_complete_description')}
              </p>
            </>
          ) : null}

          <p className="typo-primary-base text-text-body inline-block mt-lg mb-md">
            <Trans t={t} i18nKey="joint.manual-review.secure_chat_description">
              <Link href={`mailto:${CUSTOMER_SERVICE_EMAIL}`}>{CUSTOMER_SERVICE_EMAIL}</Link>
            </Trans>
          </p>
          <div className="flex flex-col md:flex-row gap-xs mb-xl">
            <Button
              variant={ButtonVariant.Secondary}
              className="w-fit w-full md:w-[200px]"
              onClick={() => {
                sendEvent('bankhjoint_enroll_manual_review_started', {
                  applicationId,
                })
                inAppChatService.open()
              }}
            >
              {t('joint.manual-review.start_chat_cta')}
            </Button>
            {!isSubmitSuccess ? (
              <Button
                variant={ButtonVariant.Primary}
                className="w-fit w-full md:w-[200px]"
                isDisabled={uploadedFiles.length === 0}
                isLoading={isSubmitting}
                onClick={() => {
                  sendEvent('bankhjoint_enroll_manual_review_docs_submitted', {
                    applicationId,
                  })
                  void handleSubmit()
                }}
              >
                {t('joint.manual-review.submit')}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  )
}
